import React, {useState, useCallback, useEffect} from 'react';
import {Box, ClickAwayListener, Popper, Slider, styled} from '@mui/material';
import {
  designSystemToken,
  Label,
  Body,
  Headline,
} from '@lightricks/react-design-system';
import elevationShadow from '@/utils/elevationShadow';
import translate from '@/utils/translate';

interface AudioMixPopperProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  avatarAudioReductionFactor: number;
  backgroundAudioReductionFactor: number;
  onAudioMixChange: (
    avatarAudioReductionFactor: number,
    backgroundAudioReductionFactor: number
  ) => void;
}

const PaperContainer = styled(Box)(() => ({
  backgroundColor: designSystemToken('semantic.bg.secondary'),
  borderRadius: '14px',
  border: `0.5px solid ${designSystemToken('semantic.bg.alpha')}`,
  boxShadow: elevationShadow(2),
  padding: '8px 16px 24px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
}));

const HeaderContainer = styled(Box)(() => ({
  width: '100%',
  borderBottom: `0.5px solid ${designSystemToken('semantic.bg.alpha')}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const Title = styled(Headline)(() => ({
  paddingBottom: '4px',
  color: designSystemToken('semantic.fg.primary'),
}));

const ControlsContainer = styled(Box)(() => ({
  padding: '0 4px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const LabelContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
}));

const LabelText = styled(Label)(() => ({
  color: designSystemToken('semantic.fg.secondary'),
}));

const ValueText = styled(Body)(() => ({
  color: designSystemToken('semantic.fg.primary'),
}));

const SliderContainer = styled(Box)(() => ({
  padding: '0 4px',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
}));

const MIDPOINT = 0.5;
const MIN_SLIDER_VALUE = 0;
const MAX_SLIDER_VALUE = 1;
const STEP_SLIDER_VALUE = 0.01;
const MAX_AUDIO_FACTOR = 1.0;
const MIN_AUDIO_FACTOR = 0;
const DISPLAY_PERCENTAGE_MULTIPLIER = 50;
const MAX_DISPLAY_PERCENTAGE = 100;
const SLIDER_TO_FACTOR_MULTIPLIER = 2;

const shouldNotForwardProps = ['trackColor', 'railColor'];

const AudioMixSlider = styled(Slider, {
  shouldForwardProp: (prop: string) => !shouldNotForwardProps.includes(prop),
})<{trackColor?: string; railColor?: string}>(({trackColor, railColor}) => ({
  'width': '212px',
  'padding': '0',
  '& .MuiSlider-rail': {
    backgroundColor:
      railColor || designSystemToken('semantic.bg.neutral-secondary'),
    height: '4px',
    borderRadius: '1000px',
    opacity: 1,
  },
  '& .MuiSlider-track': {
    backgroundColor: trackColor || designSystemToken('semantic.fg.primary'),
    height: '4px',
    border: 'none',
    borderRadius: '1000px',
  },
  '& .MuiSlider-thumb': {
    'width': '16px',
    'height': '16px',
    'backgroundColor': designSystemToken('semantic.bg.secondary'),
    'border': `0.5px solid ${designSystemToken('semantic.fg.primary')}`,
    'borderRadius': '50%',
    'boxShadow': 'none',
    '&:hover, &.Mui-focusVisible': {
      boxShadow: 'none',
    },
  },
}));

/**
 * AudioMixPopper component for adjusting audio mix between Original and Avatar audio.
 *
 * The slider controls audio reduction factors with the following behavior:
 * - 0% slider (left): Original=1.0, Avatar=0.0
 * - 50% slider (midpoint): Original=1.0, Avatar=1.0
 * - 100% slider (right): Original=0.0, Avatar=1.0
 * - 0-50% slider: Original stays at 1.0, Avatar goes from 0 to 1
 * - 50-100% slider: Original goes from 1.0 to 0, Avatar stays at 1.0
 *
 * Display percentages (simplified, always add up to 100%):
 * - Original: 100% × (1 - sliderPosition) → Left (0%) = 100%, Right (100%) = 0%
 * - Avatar: 100% × sliderPosition → Left (0%) = 0%, Right (100%) = 100%
 *
 * @param props - Component props
 * @param props.open - Whether the popper is open
 * @param props.anchorEl - Element to anchor the popper to
 * @param props.onClose - Callback when popper should close
 * @param props.avatarAudioReductionFactor - Avatar audio reduction factor (0-1)
 * @param props.backgroundAudioReductionFactor - Original/background audio reduction factor (0-1)
 * @param props.onAudioMixChange - Callback when audio mix values change
 */
function AudioMixPopper({
  open,
  anchorEl,
  onClose,
  avatarAudioReductionFactor,
  backgroundAudioReductionFactor,
  onAudioMixChange,
}: AudioMixPopperProps) {
  const getSliderValueFromFactors = useCallback(() => {
    if (
      backgroundAudioReductionFactor === MAX_AUDIO_FACTOR &&
      avatarAudioReductionFactor < MAX_AUDIO_FACTOR
    ) {
      return avatarAudioReductionFactor * MIDPOINT;
    }

    return (
      MIDPOINT + (MAX_AUDIO_FACTOR - backgroundAudioReductionFactor) * MIDPOINT
    );
  }, [avatarAudioReductionFactor, backgroundAudioReductionFactor]);

  const [sliderValue, setSliderValue] = useState(getSliderValueFromFactors);

  useEffect(() => {
    const newSliderValue = getSliderValueFromFactors();
    setSliderValue(newSliderValue);
  }, [getSliderValueFromFactors]);

  const handleSliderChange = useCallback(
    (_: Event, newValue: number | number[]) => {
      const sliderPercent = Array.isArray(newValue) ? newValue[0] : newValue;
      setSliderValue(sliderPercent);

      let newAvatarFactor: number;
      let newBackgroundFactor: number;

      if (sliderPercent <= MIDPOINT) {
        newBackgroundFactor = MAX_AUDIO_FACTOR;
        newAvatarFactor = sliderPercent * SLIDER_TO_FACTOR_MULTIPLIER;
      } else {
        newBackgroundFactor =
          SLIDER_TO_FACTOR_MULTIPLIER -
          sliderPercent * SLIDER_TO_FACTOR_MULTIPLIER;
        newAvatarFactor = MAX_AUDIO_FACTOR;
      }

      onAudioMixChange(newAvatarFactor, newBackgroundFactor);
    },
    [onAudioMixChange]
  );

  const getOriginalDisplayPercentage = (sliderPosition: number) => {
    return Math.round(MAX_DISPLAY_PERCENTAGE * (1 - sliderPosition));
  };

  const getAvatarDisplayPercentage = (sliderPosition: number) => {
    return Math.round(MAX_DISPLAY_PERCENTAGE * sliderPosition);
  };

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom"
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 8],
          },
        },
      ]}
      sx={{zIndex: 1300}}
    >
      <ClickAwayListener onClickAway={onClose}>
        <PaperContainer>
          <HeaderContainer>
            <Title size="xxs">{translate('Audio Mix')}</Title>
          </HeaderContainer>

          <ControlsContainer>
            <LabelContainer sx={{alignItems: 'flex-start'}}>
              <LabelText size="xxs">{translate('Original')}</LabelText>
              <ValueText size="md">
                {`${getOriginalDisplayPercentage(sliderValue)}%`}
              </ValueText>
            </LabelContainer>

            <LabelContainer sx={{alignItems: 'flex-end'}}>
              <LabelText size="xxs">{translate('Avatar')}</LabelText>
              <ValueText size="md">
                {`${getAvatarDisplayPercentage(sliderValue)}%`}
              </ValueText>
            </LabelContainer>
          </ControlsContainer>

          <SliderContainer>
            <AudioMixSlider
              value={sliderValue}
              onChange={handleSliderChange}
              min={MIN_SLIDER_VALUE}
              max={MAX_SLIDER_VALUE}
              step={STEP_SLIDER_VALUE}
              trackColor="transparent"
              railColor={designSystemToken('semantic.bg.neutral-secondary')}
              aria-label="Audio Mix balance"
            />
          </SliderContainer>
        </PaperContainer>
      </ClickAwayListener>
    </Popper>
  );
}

export default AudioMixPopper;
